/**
 * 7 阶段感知模块
 *
 * 根据当前日期判定家长所处的"中考时刻"阶段，
 * 用于切换首屏主推内容、文案、引导动作。
 *
 * 邹城中考关键时间点（基于近三年规律，年度可调）：
 *  - 中考考试：6/13–15
 *  - 出分：6/24 前后
 *  - 公告 / 填报开始：6/25–7/3 区间
 *  - 填报截止：7/4–7/8 区间
 *  - 录取公布：7/10 起
 *
 * 这些日期作为可配置常量（按年放在 stage-config.json）。
 */

export type LifeStage =
  | 'pre_exam'        // ① 考前很久（5月前）
  | 'near_exam'       // ② 临近考试（5月-考前）
  | 'in_exam'         // ③ 考试中（6/13-15）
  | 'wait_score'      // ④ 考后出分 / 分段表前
  | 'after_score'     // ⑤ 出分后填报前
  | 'fill_window'     // ⑥ 填报窗口
  | 'after_admission' // ⑦ 录取后

interface StageConfig {
  year: number
  /** 考试开始时刻（年中第一科一般早上 9:00） */
  examStart: string         // 'YYYY-MM-DDTHH:mm'
  /** 考试结束时刻（最后一科一般 17:00 收卷） */
  examEnd: string
  /** 出分日（待定，先按近三年规律估计） */
  scoreReleaseDate: string
  fillStart: string
  fillEnd: string
  admissionDate: string
  /** 开学日（9/1 上午 8:00） */
  schoolStartDate: string
}

/** 当前年份的关键时间点（按年配置） */
const STAGE_CONFIGS: Record<number, StageConfig> = {
  2026: {
    year: 2026,
    examStart: '2026-06-13T09:00',
    examEnd: '2026-06-15T17:00',
    scoreReleaseDate: '2026-06-24',
    fillStart: '2026-06-25',
    fillEnd: '2026-07-03',
    admissionDate: '2026-07-10',
    schoolStartDate: '2026-09-01T08:00'
  },
  2025: {
    year: 2025,
    examStart: '2025-06-13T09:00',
    examEnd: '2025-06-15T17:00',
    scoreReleaseDate: '2025-06-25',
    fillStart: '2025-06-25',
    fillEnd: '2025-07-03',
    admissionDate: '2025-07-10',
    schoolStartDate: '2025-09-01T08:00'
  }
}

/** 把一份配置的所有日期按年份平移（替换日期串前 4 位年份） */
function shiftConfigYear(cfg: StageConfig, year: number): StageConfig {
  const shift = (s: string) => s.replace(/^\d{4}/, String(year))
  return {
    year,
    examStart: shift(cfg.examStart),
    examEnd: shift(cfg.examEnd),
    scoreReleaseDate: shift(cfg.scoreReleaseDate),
    fillStart: shift(cfg.fillStart),
    fillEnd: shift(cfg.fillEnd),
    admissionDate: shift(cfg.admissionDate),
    schoolStartDate: shift(cfg.schoolStartDate)
  }
}

function getCurrentConfig(now: Date = new Date()): StageConfig {
  const y = now.getFullYear()
  if (STAGE_CONFIGS[y]) return STAGE_CONFIGS[y]
  // 无该年配置：用"最近的可用年份"配置按年份平移到本年。
  // 否则跨年后所有里程碑都落在过去 → 会一直误显示"录取后"。
  // 平移后日期为估算（沿用近年规律），公告发布后应补本年精确配置。
  const latest = Object.keys(STAGE_CONFIGS)
    .map(Number)
    .sort((a, b) => a - b)
    .pop()!
  return shiftConfigYear(STAGE_CONFIGS[latest], y)
}

function daysBetween(a: Date, b: Date): number {
  const ms = b.getTime() - a.getTime()
  return Math.floor(ms / 86400000)
}

/** 解析"日期"或"日期+时刻"字符串为东八区 Date */
function parseDate(s: string): Date {
  return new Date((s.includes('T') ? s : s + 'T00:00:00') + '+08:00')
}

/**
 * 倒计时显示精度：
 *  - ms        ：毫秒级动态，HH:MM:SS.cc 一直跳（用于考试倒计时）
 *  - day       ：精确到天（用于填报、开学等固定日期）
 *  - approx_day：约 N 天（用于出分、填报等待定日期）
 */
export type MilestonePrecision = 'ms' | 'day' | 'approx_day'

export interface StageInfo {
  stage: LifeStage
  label: string
  /** 倒计时目标时刻；null 表示已过 */
  milestoneAt: Date | null
  /** 倒计时显示精度 */
  milestonePrecision: MilestonePrecision
  /** 下一个关键日期的中文描述 */
  nextMilestoneLabel: string
  /** 距离下一个关键日期的天数（向后兼容） */
  daysToNextMilestone: number
  /** 首屏的主推标语 */
  heroHint: string
  /** 是否可以测算（公告已发布） */
  canCalculate: boolean
  /** 是否邀请回填 */
  showFeedback: boolean
}

export function detectStage(now: Date = new Date(), policyHasData: boolean = true): StageInfo {
  const cfg = getCurrentConfig(now)
  const examStart = parseDate(cfg.examStart)
  const examEnd = parseDate(cfg.examEnd)
  const scoreRelease = parseDate(cfg.scoreReleaseDate)
  const fillStart = parseDate(cfg.fillStart)
  const fillEnd = parseDate(cfg.fillEnd)
  const admission = parseDate(cfg.admissionDate)
  const schoolStart = parseDate(cfg.schoolStartDate)

  // 阶段判定 —— 注意「考试中」要按时刻（不能按日期），毫秒倒计时需要这样
  // 其它阶段（填报、出分）以日历日为单位，今天 00:00 为基准
  const today = new Date(now.getFullYear(), now.getMonth(), now.getDate())

  // ⑦ 录取后 → 9/1 开学倒计时（天）
  if (today >= admission) {
    return {
      stage: 'after_admission',
      label: '录取已结束',
      milestoneAt: schoolStart,
      milestonePrecision: 'day',
      daysToNextMilestone: Math.max(0, daysBetween(today, schoolStart)),
      nextMilestoneLabel: '距离 9 月 1 日开学',
      heroHint: '欢迎回来留下你的真实录取结果，为明年的学弟学妹提供更准的预测。',
      canCalculate: policyHasData,
      showFeedback: true
    }
  }

  // ⑥ 填报窗口中 → 填报截止倒计时（天）
  if (today >= fillStart && today < fillEnd) {
    return {
      stage: 'fill_window',
      label: '填报窗口期',
      milestoneAt: fillEnd,
      milestonePrecision: 'day',
      daysToNextMilestone: Math.max(0, daysBetween(today, fillEnd)),
      nextMilestoneLabel: '距离填报截止',
      heroHint: '填报窗口正在进行。输入分数，立刻看到五校录取概率与建议。',
      canCalculate: policyHasData,
      showFeedback: false
    }
  }

  // ⑤ 出分后填报前 → 填报开始倒计时（约 N 天，因为填报开始日期年年微调）
  if (today >= scoreRelease && today < fillStart) {
    return {
      stage: 'after_score',
      label: policyHasData ? '已出分 · 等待填报' : '已出分 · 待分段表',
      milestoneAt: fillStart,
      milestonePrecision: 'approx_day',
      daysToNextMilestone: Math.max(0, daysBetween(today, fillStart)),
      nextMilestoneLabel: policyHasData ? '距离填报开始（约）' : '等待官方分段表',
      heroHint: policyHasData
        ? '今年公告已发布。输入孩子的分数，开始测算五校录取概率。'
        : '今年中考分数已出，等待官方分段表发布并录入后，即可开放 2026 版正式测算。',
      canCalculate: policyHasData,
      showFeedback: false
    }
  }

  // ④ 考后出分前 → 出分倒计时（约 N 天，出分日待定）
  if (now > examEnd && today < scoreRelease) {
    return {
      stage: 'wait_score',
      label: '等待出分',
      milestoneAt: scoreRelease,
      milestonePrecision: 'approx_day',
      daysToNextMilestone: Math.max(0, daysBetween(today, scoreRelease)),
      nextMilestoneLabel: '距离预计出分（约）',
      heroHint: '考试已结束，正在等待出分。先看看近三年的分数段和报名规则。',
      canCalculate: policyHasData,
      showFeedback: false
    }
  }

  // ③ 考试中 → 考试结束倒计时（毫秒级）
  if (now >= examStart && now <= examEnd) {
    return {
      stage: 'in_exam',
      label: '考试中',
      milestoneAt: examEnd,
      milestonePrecision: 'ms',
      daysToNextMilestone: Math.max(0, daysBetween(today, examEnd)),
      nextMilestoneLabel: '距离考试结束',
      heroHint: '孩子在考场，家长在等待。先了解今年的填报规则，做好准备。',
      canCalculate: policyHasData,
      showFeedback: false
    }
  }

  // ② 临近考试（30 天内）→ 中考开始倒计时（毫秒级）
  const daysToExam = daysBetween(today, examStart)
  if (now < examStart && daysToExam <= 30) {
    return {
      stage: 'near_exam',
      label: '临近考试',
      milestoneAt: examStart,
      milestonePrecision: 'ms',
      daysToNextMilestone: Math.max(0, daysToExam),
      nextMilestoneLabel: '距离中考开始',
      heroHint: '中考临近。可基于往年公告先做演练，今年公告发布后再以新数据复测。',
      canCalculate: policyHasData,
      showFeedback: false
    }
  }

  // ① 考前很久 → 中考开始倒计时（毫秒级，但视觉上以"天"为主元）
  return {
    stage: 'pre_exam',
    label: '考前准备',
    milestoneAt: examStart,
    milestonePrecision: 'ms',
    daysToNextMilestone: Math.max(0, daysToExam),
    nextMilestoneLabel: '距离今年中考',
    heroHint: '欢迎使用邹城中考志愿测算。可基于往年公告先做演练，今年公告发布后再以新数据复测。',
    canCalculate: policyHasData,
    showFeedback: false
  }
}

export const STAGE_LABELS: Record<LifeStage, string> = {
  pre_exam: '考前准备',
  near_exam: '临近考试',
  in_exam: '考试中',
  wait_score: '等待出分',
  after_score: '已出分',
  fill_window: '填报窗口',
  after_admission: '录取后'
}
